import { PostgrestError } from '@supabase/supabase-js';

export const SUPABASE_PAGE_SIZE = 1000;

interface PageResult<T> {
  data: T[] | null;
  error: PostgrestError | null;
  count?: number | null;
}

/**
 * Supabase 的 REST API 單次最多只回傳設定的筆數（預設 1000），沒有分頁的 select 超過就會被無聲截斷。
 * 這裡依第一頁回傳的總筆數逐頁抓完；伺服器若把單次上限設得比 pageSize 小也不會提早停止。
 * fetchPage 需要用 select('*', { count: 'exact' }) 並搭配穩定的排序，分頁之間才不會重複或遺漏。
 */
export async function fetchAllRows<T>(
  fetchPage: (from: number, to: number) => PromiseLike<PageResult<T>>,
  pageSize = SUPABASE_PAGE_SIZE
): Promise<{ data: T[]; error: PostgrestError | null }> {
  const rows: T[] = [];
  let total: number | null = null;

  while (total === null || rows.length < total) {
    const { data, error, count } = await fetchPage(rows.length, rows.length + pageSize - 1);
    if (error) return { data: rows, error };

    const page = data ?? [];
    total ??= count ?? null;
    rows.push(...page);

    // 拿不到總筆數時退回以「這頁不滿」判斷結束；空頁一律結束，避免資料在抓取途中被刪除時無限迴圈
    if (page.length === 0 || (total === null && page.length < pageSize)) break;
  }

  return { data: rows, error: null };
}
